import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import PromoStrip from "@/components/PromoStrip";
import { Cpu, Radio, Trophy, ArrowRight, ScanLine } from "lucide-react";

const CAT_IMG = {
  rc: "https://images.unsplash.com/photo-1613404198873-14a30017543a?crop=entropy&cs=srgb&fm=jpg&q=85",
  sim: "https://images.unsplash.com/photo-1743649978995-c76212449e15?crop=entropy&cs=srgb&fm=jpg&q=85",
};

export default function Home() {
  const [content, setContent] = useState(null);
  const [champs, setChamps] = useState([]);
  const { tick } = useActivity();

  useEffect(() => {
    api.get("/content/home").then((r) => setContent(r.data)).catch(() => {});
    api.get("/championships").then((r) => setChamps(r.data)).catch(() => {});
  }, [tick]);

  const rcCount = champs.filter((c) => c.category === "rc").length;
  const simCount = champs.filter((c) => c.category === "sim").length;

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden grain">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${content?.hero_image || CAT_IMG.rc})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/80 to-zinc-950/40" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="text-xs font-bold tracking-[0.3em] uppercase text-cyan-400" data-testid="hero-eyebrow">
              RC · Simulatori · Campionati
            </span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-zinc-50 leading-[0.95]">
              {content?.title || "TEAM TUONO"}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-zinc-300 max-w-xl">
              {content?.subtitle || "Organizza gare, gestisci classifiche e assegna punti automaticamente da una foto."}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/campionati"
                data-testid="hero-cta-championships"
                className="group inline-flex items-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-sm tracking-[0.15em] uppercase px-7 py-4 -skew-x-12 hover:bg-yellow-300 transition-colors duration-200"
              >
                <span className="inline-flex items-center gap-2 skew-x-12">
                  Vedi Campionati <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                </span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-y border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-800">
          {[
            { icon: ScanLine, t: "Punteggio da foto", d: "Carica la foto del tabellone: l'AI riconosce nomi e posizioni." },
            { icon: Trophy, t: "Classifiche automatiche", d: "Punti F1 o personalizzati, aggregati per campionato." },
            { icon: Cpu, t: "RC + Simulatori", d: "PlayStation, Xbox, PC e gare RC fisiche, tutto in un posto." },
          ].map((f, i) => (
            <div key={i} className="p-8 flex flex-col gap-3">
              <f.icon className="h-7 w-7 text-yellow-400 text-glow-yellow" />
              <h3 className="font-heading text-lg font-bold text-zinc-50">{f.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight uppercase text-zinc-50 mb-10">
          Scegli il tuo <span className="text-cyan-400">mondo</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <CategoryCard
            to="/campionati?cat=rc"
            img={CAT_IMG.rc}
            icon={Radio}
            label="Corse RC fisiche"
            count={rcCount}
            testid="cat-rc"
          />
          <CategoryCard
            to="/campionati?cat=sim"
            img={CAT_IMG.sim}
            icon={Cpu}
            label="Simulatori PS / Xbox / PC"
            count={simCount}
            testid="cat-sim"
          />
        </div>
      </section>

      <PromoStrip />
    </div>
  );
}

function CategoryCard({ to, img, icon: Icon, label, count, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group relative h-72 overflow-hidden border border-zinc-800 bg-zinc-900">
      <img src={img} alt={label}
        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />
      <div className="relative z-10 h-full flex flex-col justify-end p-8">
        <Icon className="h-8 w-8 text-yellow-400 mb-3 text-glow-yellow" />
        <h3 className="font-heading text-2xl font-bold uppercase text-zinc-50 tracking-tight">{label}</h3>
        <p className="mt-2 text-sm text-zinc-400">
          <span className="font-display text-yellow-400">{count}</span> campionat{count === 1 ? "o" : "i"}
        </p>
      </div>
    </Link>
  );
}
